/**
 * Field — the tech's own view, read from the device.
 *
 * This screen no longer needs a connection. Everything on it comes out of the
 * local store, which the sync engine keeps current when there is signal; in a
 * comms room it shows the same run sheet it showed in the van.
 *
 * Deliberately still not a second app: it is the same data as the office sees,
 * filtered to "mine, today", with the actions a tech needs one-handed.
 */
import { Link } from "react-router-dom";

import type { JobOut } from "../lib/types";

import { useAuth } from "../lib/auth";
import { useLocal } from "../lib/local";
import { useOffline } from "../lib/offline";
import { money, qty, slaLabel, time, titleCase, untilMinutes } from "../lib/format";
import * as repo from "../lib/repo";
import { splitDone } from "../lib/daywork";
import { Identify } from "../components/Identify";
import { Card, Chip, Empty, Loading, PageHeader, type Tone } from "../components/ui";

function todayInAdelaide(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Adelaide" }).format(new Date());
}

/** Local midnight to local midnight, compared against a UTC timestamp. */
function isToday(iso: string | null): boolean {
  if (!iso) return false;
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Adelaide" }).format(new Date(iso)) ===
    todayInAdelaide();
}

export default function Field() {
  const { session } = useAuth();
  const { state, sync, syncing } = useOffline();

  const userId = session?.userId ?? "";
  const { data: jobs, isPending } = useLocal(() => repo.myJobs(userId), [userId]);
  const { data: stock } = useLocal(() => repo.vanStock(), []);

  if (isPending) return <Loading what="Opening your day" />;

  const mine = jobs ?? [];
  const today = mine.filter((j) => isToday(j.scheduled_start));
  // Unscheduled work assigned to this tech, and anything still open from
  // earlier in the week. A job that ran over yesterday is still theirs.
  const alsoOpen = mine.filter(
    (j) => !isToday(j.scheduled_start) && !["completed", "invoiced", "cancelled"].includes(j.status),
  );

  // Finished work leaves the list: Today is what is still to do (F1).
  const { todo, done } = splitDone(today);
  const next = todo[0];
  const carrying = state?.carrying ?? 0;

  return (
    <div className="page">
      <PageHeader title="Your day">
        {today.length === 0
          ? "Nothing booked to you today."
          : todo.length === 0
            ? `All done — ${done.length} finished today.`
            : `${todo.length} job${todo.length === 1 ? "" : "s"} still to do${done.length > 0 ? `, ${done.length} done` : ""}.`}
      </PageHeader>

      {next && (
        <Card title="Up next" subtitle={time(next.scheduled_start)}>
          <h3 style={{ fontSize: 17, marginBottom: 6 }}>{next.title}</h3>
          <div className="chip-row">
            <Chip tone="neutral">{titleCase(next.type)}</Chip>
            <Chip tone="info">{titleCase(next.status)}</Chip>
            {next.sla_due_at && (
              <Chip tone={slaTone(next.sla_due_at)} dot>
                {slaLabel(next.sla_due_at)}
              </Chip>
            )}
          </div>
          <div className="btn-row" style={{ marginTop: 14 }}>
            <Link className="btn btn-primary" to={`/jobs/${next.id}`}>
              Open the job
            </Link>
          </div>
        </Card>
      )}

      <Card title="Today" padded={false}>
        {today.length === 0 ? (
          <Empty title="Nothing booked">
            When the office assigns you work it appears here, soonest first.
          </Empty>
        ) : todo.length === 0 ? (
          <Empty title="All done">Everything on your run today is finished.</Empty>
        ) : (
          <div className="stack" style={{ padding: 12 }}>
            {todo.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
          </div>
        )}
      </Card>

      {done.length > 0 && (
        <Card padded={false}>
          <details className="done-today">
            <summary>Done today ({done.length})</summary>
            <div className="stack" style={{ padding: 12 }}>
              {done.map((job) => (
                <JobRow key={job.id} job={job} done />
              ))}
            </div>
          </details>
        </Card>
      )}

      {alsoOpen.length > 0 && (
        <Card title="Still open" subtitle="Yours, from before today" padded={false}>
          <div className="stack" style={{ padding: 12 }}>
            {alsoOpen.map((job) => (
              <JobRow key={job.id} job={job} />
            ))}
          </div>
        </Card>
      )}

      {/* Above the van stock on purpose: a tech standing in front of
          something they cannot identify wants this first, and it is the one
          card here that answers a question rather than listing work. */}
      <Identify />

      <Card title="On the van" subtitle="What this device thinks you're carrying">
        {!stock || stock.length === 0 ? (
          <p className="tiny">No stock levels on this device yet.</p>
        ) : (
          <div className="chip-row">
            {stock.slice(0, 12).map(({ item, level }) => (
              <Chip key={String(level.id)} tone={Number(level.qty) > 0 ? "neutral" : "warn"}>
                {String(item.description)} · {qty(Number(level.qty))}
              </Chip>
            ))}
          </div>
        )}
      </Card>

      <Card title="This device">
        <p className="tiny" style={{ lineHeight: 1.7 }}>
          Everything on this screen is read from the phone, so it works with no
          signal. You're carrying <b>{carrying.toLocaleString()}</b> records
          {state?.hydratedAt
            ? `, current as of ${new Date(state.hydratedAt).toLocaleString("en-AU", {
                weekday: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}`
            : ""}
          {state && state.pending > 0 ? ` · ${state.pending} change${state.pending > 1 ? "s" : ""} waiting to send` : ""}.
        </p>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <button type="button" className="btn btn-sm" onClick={() => void sync()} disabled={syncing}>
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>
      </Card>
    </div>
  );
}

function JobRow({ job, done = false }: { job: JobOut; done?: boolean }) {
  return (
    <Link to={`/jobs/${job.id}`} className={`joblet joblet-info${done ? " joblet-done" : ""}`}>
      <div className="joblet-main">
        <span className="joblet-title">{job.title}</span>
        <span className="joblet-meta">
          <span className="mono">{job.number}</span>
          {job.scheduled_start && ` · ${time(job.scheduled_start)}`}
          {job.quoted_cents > 0 && ` · ${money(job.quoted_cents)}`}
        </span>
      </div>
      <Chip tone="neutral">{titleCase(job.status)}</Chip>
    </Link>
  );
}

function slaTone(iso: string): Tone {
  const mins = untilMinutes(iso);
  if (mins === null) return "neutral";
  if (mins < 0) return "crit";
  if (mins < 120) return "warn";
  return "ok";
}
